import { useQuery } from "@apollo/client";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { gql } from "generated-graphql";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { useCallback, useMemo } from "react";

import {
  ShowFilterId,
  SHOW_FILTER_ORDER,
  SHOW_FILTERS,
  applyShowFilterToSearch,
  detectActiveShowFilter,
  formatShowFilterLabel,
} from "./manifestShowFilters";
import { mergeDefaultAndOmnisearch } from "./mergeDefaultAndOmnisearch";

const PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY = gql(`
  query ProcessedManifestShowFilterCounts($search: String) {
    processedManifestShowFilterCounts(search: $search) {
      filterId
      count
    }
  }
`);

interface ManifestShowFilterPickerViewProps {
  defaultSearch: string;
  /** The active omnisearch string, and the setter that replaces it. */
  omnisearch: string;
  onApply: (nextOmnisearch: string) => void;
  /**
   * True on the merged Shipment Documents list, which holds bills of lading as
   * well as manifests.
   *
   * Changes two things about the unfiltered option, and nothing else. Its label
   * stops saying "manifests", because the page around it says Shipment
   * Documents. And it stops showing a count, because every count here comes
   * from `PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY`: correct for the
   * manifest-only filters below it, wrong for a bucket that also holds
   * documents, and visibly wrong the moment a bill of lading is saved and the
   * row appears while the number does not move.
   */
  merged?: boolean;
}

/**
 * The picker itself, with no opinion about where omnisearch lives. V2's grid
 * keeps its omnisearch in `OmnisearchDataContext` and the merged V3 grid keeps
 * its own in the omnisearch-v2 URL store, so the state is passed in rather than
 * read from a context only one of the two screens has.
 */
function ManifestShowFilterPickerView({
  defaultSearch,
  omnisearch,
  onApply,
  merged = false,
}: ManifestShowFilterPickerViewProps) {
  const { data, loading } = useQuery(PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY, {
    variables: { search: defaultSearch },
    fetchPolicy: "cache-and-network",
  });

  const countByFilterId = useMemo(() => {
    const map = new Map<ShowFilterId, number>();
    for (const row of data?.processedManifestShowFilterCounts ?? []) {
      if (SHOW_FILTER_ORDER.includes(row.filterId as ShowFilterId)) {
        map.set(row.filterId as ShowFilterId, row.count);
      }
    }
    return map;
  }, [data]);

  const mergedSearch = useMemo(
    () => mergeDefaultAndOmnisearch(defaultSearch, omnisearch),
    [defaultSearch, omnisearch]
  );

  const activeFilterId = useMemo(() => detectActiveShowFilter(mergedSearch), [mergedSearch]);

  const getOptionLabel = useCallback(
    (option: ShowFilterId) => {
      const def = SHOW_FILTERS[option];
      const unfilteredOnMergedList = merged && option === "allManifests";
      const label = unfilteredOnMergedList ? "All documents" : def.label;
      const count = countByFilterId.get(option);
      if (unfilteredOnMergedList || (loading && count == null)) {
        return label;
      }
      return formatShowFilterLabel(label, count ?? 0);
    },
    [countByFilterId, loading, merged]
  );

  const handleChange = useCallback(
    (_: unknown, value: ShowFilterId) => {
      onApply(applyShowFilterToSearch(omnisearch, value));
    },
    [omnisearch, onApply]
  );

  return (
    <Autocomplete<ShowFilterId, false, true, false>
      size="small"
      options={SHOW_FILTER_ORDER}
      value={activeFilterId}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        const { key, ...liProps } = props;
        return (
          <Tooltip
            key={key}
            title={SHOW_FILTERS[option].description}
            placement="right"
            enterDelay={300}
          >
            <li {...liProps}>{getOptionLabel(option)}</li>
          </Tooltip>
        );
      }}
      renderInput={(params) => <TextField {...params} label="Show" variant="standard" />}
      disableClearable
      sx={{ minWidth: 240 }}
    />
  );
}

export interface ManifestShowFilterPickerProps {
  defaultSearch: string;
}

/**
 * Mutually exclusive Show filter dropdown (Year picker styling). Counts reflect
 * the page scope (tenant + year) only; choosing a filter updates omnisearch.
 */
export function ManifestShowFilterPicker({ defaultSearch }: ManifestShowFilterPickerProps) {
  const { omnisearch, setOmnisearchImmediate } = useOmnisearch();

  return (
    <ManifestShowFilterPickerView
      defaultSearch={defaultSearch}
      omnisearch={omnisearch}
      onApply={setOmnisearchImmediate}
    />
  );
}

/**
 * The same picker for the merged Manifests list, reading the omnisearch-v2 URL
 * store. Same presets: the merged reader answers every one of them.
 */
export function MergedManifestShowFilterPicker({ defaultSearch }: ManifestShowFilterPickerProps) {
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();

  return (
    <ManifestShowFilterPickerView
      defaultSearch={defaultSearch}
      omnisearch={omnisearch}
      onApply={setOmnisearchImmediate}
      merged
    />
  );
}
